// src/pages/landing/PrivacyPolicyPage.js
import React from 'react';
import LandingLayout from '../../components/landing/LandingLayout';
import { useLanguage } from '../../context/LanguageContext';
import { C, TYPE, SP, W } from '../../components/landing/tokens';
import { Link } from 'react-router-dom';

const LAST_UPDATED = '27 June 2026';

const PrivacyPolicyPage = () => {
    const { isRTL } = useLanguage();

    // F16 — skin only. The legal prose is deliberate text and is not touched.
    const sectionTitle = { ...TYPE.title, color: C.ink };
    const body = { ...TYPE.body, color: C.inkMuted };
    const legalLink = { color: C.ink, textDecoration: 'none', borderBlockEnd: `1px solid ${C.lineStrong}` };

    return (
        <div style={{ background: C.paper, color: C.ink }} dir={isRTL ? 'rtl' : 'ltr'}>
            <LandingLayout>
                {/* Hero */}
                <section className="py-16 lg:py-24" style={{ background: C.paperDeep }}>
                    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                        <h1 style={{ ...TYPE.displayM, color: C.ink, marginBlockEnd: SP.sm }}>
                            Privacy Policy
                        </h1>
                        <p style={{ ...TYPE.caption, color: C.inkFaint }}>
                            Last updated: {LAST_UPDATED}
                        </p>
                    </div>
                </section>

                {/* Content */}
                <section className="py-16" style={{ background: C.paper }}>
                    <div className="mx-auto px-4 sm:px-6 lg:px-8 space-y-10" style={{ maxWidth: W.prose }}>

                        <div>
                            <p className="leading-relaxed" style={body}>
                                This Privacy Policy explains how <strong>AQANA FINANCE LTD</strong> ("Aqana", "we",
                                "us", or "our"), a company registered in England &amp; Wales, collects, uses, stores,
                                and protects information in connection with the Aqana point-of-sale and business
                                analytics platform (the "Service"). By using the Service you agree to the practices
                                described below.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold mb-4" style={sectionTitle}>1. Who We Are</h2>
                            <p className="leading-relaxed" style={body}>
                                Aqana provides point-of-sale, operations, and analytics software for restaurants and
                                hospitality businesses. The data controller for personal data processed through the
                                Service is AQANA FINANCE LTD. Our full registered office address is available on
                                request by emailing <a href="mailto:admin@aqana.uk" style={legalLink}>admin@aqana.uk</a>.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold mb-4" style={sectionTitle}>2. Information We Collect</h2>
                            <p className="leading-relaxed mb-4" style={body}>We collect the following categories of information:</p>
                            <ul className="list-disc list-inside space-y-3 leading-relaxed" style={body}>
                                <li>
                                    <strong>Account &amp; business data</strong> — the name, email address, business
                                    name, and contact details you provide when you register and configure your account.
                                </li>
                                <li>
                                    <strong>Operational data</strong> — the sales, products, staff, and transaction
                                    records you enter into, or generate through, the Service.
                                </li>
                                <li>
                                    <strong>Connected Meta (Facebook &amp; Instagram) data</strong> — if you choose to
                                    connect a Facebook Page or Instagram account, we retrieve the list of Pages and
                                    accounts you manage and their organic engagement and insight metrics. This is done
                                    through the Meta Graph API using the permissions <code>pages_show_list</code>,{' '}
                                    <code>pages_read_engagement</code>, <code>read_insights</code>,{' '}
                                    <code>instagram_basic</code>, <code>instagram_manage_insights</code>, and{' '}
                                    <code>business_management</code>. We access insight and engagement metrics only —
                                    we do not post on your behalf, read private messages, or run advertising.
                                </li>
                            </ul>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold mb-4" style={sectionTitle}>3. How We Use Your Information</h2>
                            <p className="leading-relaxed" style={body}>
                                We use the information solely to operate and provide the Service: to display analytics
                                and reporting inside your Aqana portal, to maintain your account, and to support you.
                                We <strong>do not</strong> sell your data, use it for advertising, or share it with third
                                parties for their own marketing purposes.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold mb-4" style={sectionTitle}>4. Storage &amp; Security</h2>
                            <p className="leading-relaxed" style={body}>
                                Data is hosted on Google Cloud infrastructure in the European Union. Access tokens for
                                connected Meta accounts are encrypted at rest. The Service is multi-tenant and data is
                                isolated per business. We apply appropriate technical and organisational measures to
                                protect against unauthorised access, loss, or disclosure.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold mb-4" style={sectionTitle}>5. Third-Party Processors</h2>
                            <p className="leading-relaxed mb-4" style={body}>
                                We rely on a small number of trusted providers to deliver the Service:
                            </p>
                            <ul className="list-disc list-inside space-y-2 leading-relaxed" style={body}>
                                <li><strong>Meta Platforms</strong> — source of connected Facebook/Instagram insight data.</li>
                                <li><strong>Google Cloud</strong> — hosting, database, and storage.</li>
                                <li><strong>Anthropic</strong> — AI-assisted analytics features.</li>
                            </ul>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold mb-4" style={sectionTitle}>6. Data Retention</h2>
                            <p className="leading-relaxed" style={body}>
                                We retain your data for as long as your account is active or as needed to provide the
                                Service. When you disconnect a Meta account or close your account, the associated data
                                is deleted as described in our{' '}
                                <Link to="/data-deletion" style={legalLink}>Data Deletion</Link> policy.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold mb-4" style={sectionTitle}>7. Your Rights</h2>
                            <p className="leading-relaxed" style={body}>
                                Subject to applicable law, you have the right to access, correct, export, or delete your
                                personal data, and to withdraw consent for connected accounts at any time. To exercise
                                these rights, see our{' '}
                                <Link to="/data-deletion" style={legalLink}>Data Deletion</Link> page or
                                contact us.
                            </p>
                        </div>

                        <div>
                            <h2 className="text-2xl font-bold mb-4" style={sectionTitle}>8. Contact Us</h2>
                            <p className="leading-relaxed" style={body}>
                                For any privacy questions or requests, contact us at{' '}
                                <a href="mailto:admin@aqana.uk" style={legalLink}>admin@aqana.uk</a>.
                            </p>
                        </div>

                    </div>
                </section>
            </LandingLayout>
        </div>
    );
};

export default PrivacyPolicyPage;
